import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '../components/ui/dialog';
import { 
  PoundSterling, 
  Users, 
  FileText, 
  Send, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Building,
  Calculator,
  Download,
  Settings,
  Shield,
  Calendar,
  CreditCard,
  TrendingUp,
  UserCheck,
  FileWarning,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

// UK Tax Constants
const TAX_CODES = ['1257L', 'BR', 'D0', 'D1', 'NT', '0T', 'K', 'S', 'C'];
const NI_CATEGORIES = ['A', 'B', 'C', 'H', 'J', 'M', 'V', 'Z'];
const STUDENT_LOAN_PLANS = [
  { value: 'none', label: 'None' },
  { value: 'plan1', label: 'Plan 1 (Pre-2012)' },
  { value: 'plan2', label: 'Plan 2 (Post-2012)' },
  { value: 'plan4', label: 'Plan 4 (Scotland)' },
  { value: 'postgrad', label: 'Postgraduate Loan' }
];
const VISA_TYPES = [
  { value: 'british_citizen', label: 'British Citizen' },
  { value: 'eu_settled', label: 'EU Settled Status' },
  { value: 'eu_pre_settled', label: 'EU Pre-Settled Status' },
  { value: 'skilled_worker', label: 'Skilled Worker Visa' },
  { value: 'student', label: 'Student Visa' },
  { value: 'graduate', label: 'Graduate Visa' },
  { value: 'family', label: 'Family Visa' },
  { value: 'other', label: 'Other' }
];

export default function UKPayrollPage() {
  const { api, user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [payrollConfigs, setPayrollConfigs] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [rtiSubmissions, setRtiSubmissions] = useState([]);
  const [rtwAlerts, setRtwAlerts] = useState({});
  const [staff, setStaff] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // Dialog states
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [runPayrollDialogOpen, setRunPayrollDialogOpen] = useState(false);
  const [rtwDialogOpen, setRtwDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  
  // Form states
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [configForm, setConfigForm] = useState({
    staff_id: '',
    payment_type: 'salary',
    annual_salary: '',
    hourly_rate: '',
    pay_frequency: 'monthly',
    tax_code: '1257L',
    ni_category: 'A',
    ni_number: '',
    pension_enrolled: true,
    pension_employee_percent: 5,
    pension_employer_percent: 3,
    pension_provider: '',
    student_loan_plan: 'none',
    postgrad_loan: false,
    bank_name: '',
    account_number: '',
    sort_code: '',
    visa_type: 'british_citizen',
    visa_expiry: '',
    share_code: '',
    cos_number: ''
  });
  
  const [rtwForm, setRtwForm] = useState({
    staff_id: '',
    visa_type: 'british_citizen',
    check_date: new Date().toISOString().split('T')[0],
    document_type: '',
    document_reference: '',
    share_code: '',
    expiry_date: '',
    notes: ''
  });

  const canManage = ['director', 'hr', 'finance'].includes(user?.role);
  const canSubmit = ['director', 'finance'].includes(user?.role);

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configsRes, recordsRes, staffRes, alertsRes] = await Promise.all([
        api.get('/payroll/config'),
        api.get('/payroll/records', { params: { month: selectedMonth, year: selectedYear } }),
        api.get('/staff'),
        api.get('/payroll/right-to-work/alerts')
      ]);
      
      setPayrollConfigs(configsRes.data);
      setPayrollRecords(recordsRes.data);
      setStaff(staffRes.data);
      setRtwAlerts(alertsRes.data);
      
      // Get summary
      try {
        const summaryRes = await api.get('/payroll/reports/summary', {
          params: { month: selectedMonth, year: selectedYear }
        });
        setSummary(summaryRes.data);
      } catch {
        setSummary(null);
      }
      
      // Get RTI submissions
      try {
        const rtiRes = await api.get('/payroll/rti/submissions', {
          params: { tax_year: '2024-25' }
        });
        setRtiSubmissions(rtiRes.data);
      } catch {
        setRtiSubmissions([]);
      }
    } catch (error) {
      console.error('Failed to fetch payroll data:', error);
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    try {
      await api.post('/payroll/config', {
        ...configForm,
        annual_salary: configForm.annual_salary ? parseFloat(configForm.annual_salary) : null,
        hourly_rate: configForm.hourly_rate ? parseFloat(configForm.hourly_rate) : null
      });
      toast.success('Payroll configuration saved');
      setConfigDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    }
  };

  const handleRunPayroll = async () => {
    try {
      const response = await api.post('/payroll/run', null, {
        params: { month: selectedMonth, year: selectedYear }
      });
      toast.success(response.data.message);
      setRunPayrollDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run payroll');
    }
  };

  const handleSubmitFPS = async () => {
    if (!confirm('Submit Full Payment Submission to HMRC? This will generate the FPS file for the selected period.')) return;
    
    try {
      const response = await api.post('/payroll/rti/fps', null, {
        params: { month: selectedMonth, year: selectedYear }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate FPS');
    }
  };

  const handleRecordRTW = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/payroll/right-to-work/${rtwForm.staff_id}`, null, {
        params: rtwForm
      });
      toast.success('Right to Work check recorded');
      setRtwDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record RTW check');
    }
  };

  const openConfigDialog = (staffMember = null) => {
    if (staffMember) {
      const existingConfig = payrollConfigs.find(c => c.staff_id === staffMember.id);
      if (existingConfig) {
        setConfigForm({
          ...configForm,
          ...existingConfig
        });
      } else {
        setConfigForm({
          ...configForm,
          staff_id: staffMember.id
        });
      }
    } else {
      setConfigForm({
        staff_id: '',
        payment_type: 'salary',
        annual_salary: '',
        hourly_rate: '',
        pay_frequency: 'monthly',
        tax_code: '1257L',
        ni_category: 'A',
        ni_number: '',
        pension_enrolled: true,
        pension_employee_percent: 5,
        pension_employer_percent: 3,
        pension_provider: '',
        student_loan_plan: 'none',
        postgrad_loan: false,
        bank_name: '',
        account_number: '',
        sort_code: '',
        visa_type: 'british_citizen',
        visa_expiry: '',
        share_code: '',
        cos_number: ''
      });
    }
    setConfigDialogOpen(true);
  };

  const totalRtwAlerts = (rtwAlerts.expired?.length || 0) + (rtwAlerts.expiring_30_days?.length || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="uk-payroll-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] flex items-center gap-2">
            <PoundSterling className="w-7 h-7" />
            UK Payroll & HMRC
          </h1>
          <p className="text-slate-500 mt-1">HMRC RTI compliant payroll with Home Office integration</p>
        </div>
        
        <div className="flex gap-2 flex-wrap items-center">
          <Select value={`${selectedMonth}`} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
            <SelectTrigger className="w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, i) => (
                <SelectItem key={i} value={`${i + 1}`}>{month}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={`${selectedYear}`} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Alerts */}
      {totalRtwAlerts > 0 && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="py-3">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <div>
                <p className="font-semibold text-red-800">Right to Work Alerts</p>
                <p className="text-sm text-red-600">
                  {rtwAlerts.expired?.length || 0} expired, {rtwAlerts.expiring_30_days?.length || 0} expiring within 30 days
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="ml-auto border-red-400 text-red-700"
                onClick={() => setActiveTab('home-office')}
              >
                View Details
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="employees">Employee Setup</TabsTrigger>
          <TabsTrigger value="payroll">Payroll Records</TabsTrigger>
          <TabsTrigger value="hmrc">HMRC / RTI</TabsTrigger>
          <TabsTrigger value="home-office">
            Home Office
            {totalRtwAlerts > 0 && <Badge variant="destructive" className="ml-1 text-xs">{totalRtwAlerts}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{payrollConfigs.length}</p>
                    <p className="text-sm text-slate-500">Employees on Payroll</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <PoundSterling className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">
                      £{summary?.total_gross_pay?.toLocaleString() || '0'}
                    </p>
                    <p className="text-sm text-slate-500">Gross Pay ({selectedMonth}/{selectedYear})</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Building className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">
                      £{((summary?.total_income_tax || 0) + (summary?.total_ni_employee || 0)).toLocaleString()}
                    </p>
                    <p className="text-sm text-slate-500">Tax + NI (Employee)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">
                      £{summary?.total_employer_cost?.toLocaleString() || '0'}
                    </p>
                    <p className="text-sm text-slate-500">Total Employer Cost</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {canManage && (
                  <Button onClick={() => setRunPayrollDialogOpen(true)} className="bg-[#1e3a5f]">
                    <Calculator className="w-4 h-4 mr-2" />
                    Run Payroll
                  </Button>
                )}
                {canSubmit && (
                  <Button onClick={handleSubmitFPS} variant="outline">
                    <Send className="w-4 h-4 mr-2" />
                    Submit FPS to HMRC
                  </Button>
                )}
                {canManage && (
                  <Button onClick={() => openConfigDialog()} variant="outline">
                    <Settings className="w-4 h-4 mr-2" />
                    Add Employee to Payroll
                  </Button>
                )}
                {canManage && (
                  <Button onClick={() => setRtwDialogOpen(true)} variant="outline">
                    <Shield className="w-4 h-4 mr-2" />
                    Record RTW Check
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent RTI Submissions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Recent HMRC Submissions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {rtiSubmissions.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No RTI submissions yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Employees</TableHead>
                      <TableHead>Total Pay</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rtiSubmissions.slice(0, 5).map((sub) => (
                      <TableRow key={sub.id}>
                        <TableCell><Badge variant="outline">{sub.submission_type}</Badge></TableCell>
                        <TableCell>{sub.period}/{sub.tax_year}</TableCell>
                        <TableCell>{sub.employees_count}</TableCell>
                        <TableCell>£{sub.total_pay?.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge className={
                            sub.status === 'accepted' ? 'bg-green-500' :
                            sub.status === 'pending' ? 'bg-yellow-500' :
                            'bg-red-500'
                          }>
                            {sub.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(sub.submission_date).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Employee Setup Tab */}
        <TabsContent value="employees" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-[#1e3a5f]">Employee Payroll Configuration</h2>
            {canManage && (
              <Button onClick={() => openConfigDialog()} className="bg-[#1e3a5f]">
                <Settings className="w-4 h-4 mr-2" />
                Add Employee
              </Button>
            )}
          </div>
          
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#1e3a5f] text-white">
                    <TableHead className="text-white">Employee</TableHead>
                    <TableHead className="text-white">Tax Code</TableHead>
                    <TableHead className="text-white">NI Category</TableHead>
                    <TableHead className="text-white">Salary/Rate</TableHead>
                    <TableHead className="text-white">Pension</TableHead>
                    <TableHead className="text-white">Visa Status</TableHead>
                    <TableHead className="text-white w-20"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payrollConfigs.map((config) => (
                    <TableRow key={config.staff_id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{config.staff_name}</TableCell>
                      <TableCell><Badge variant="outline">{config.tax_code}</Badge></TableCell>
                      <TableCell>{config.ni_category}</TableCell>
                      <TableCell>
                        {config.payment_type === 'salary' 
                          ? `£${config.annual_salary?.toLocaleString()}/yr`
                          : `£${config.hourly_rate}/hr`}
                      </TableCell>
                      <TableCell>
                        {config.pension_enrolled ? (
                          <span className="text-green-600">{config.pension_employee_percent}% / {config.pension_employer_percent}%</span>
                        ) : (
                          <span className="text-slate-400">Not enrolled</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          config.visa_type === 'british_citizen' ? 'bg-green-500' :
                          config.visa_type?.includes('settled') ? 'bg-blue-500' :
                          'bg-amber-500'
                        }>
                          {VISA_TYPES.find(v => v.value === config.visa_type)?.label || config.visa_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {canManage && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setConfigForm(config);
                              setConfigDialogOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payroll Records Tab */}
        <TabsContent value="payroll" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-[#1e3a5f]">Payroll Records - {selectedMonth}/{selectedYear}</h2>
            {canManage && (
              <Button onClick={() => setRunPayrollDialogOpen(true)} className="bg-[#1e3a5f]">
                <Calculator className="w-4 h-4 mr-2" />
                Run Payroll
              </Button>
            )}
          </div>
          
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#1e3a5f]">
                    <TableHead className="text-white">Employee</TableHead>
                    <TableHead className="text-white">Gross Pay</TableHead>
                    <TableHead className="text-white">Income Tax</TableHead>
                    <TableHead className="text-white">NI (Employee)</TableHead>
                    <TableHead className="text-white">Pension</TableHead>
                    <TableHead className="text-white">Deductions</TableHead>
                    <TableHead className="text-white">Net Pay</TableHead>
                    <TableHead className="text-white">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payrollRecords.map((record) => (
                    <TableRow key={record.id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{record.staff_name}</TableCell>
                      <TableCell>£{record.gross_pay?.toLocaleString()}</TableCell>
                      <TableCell>£{record.income_tax?.toLocaleString()}</TableCell>
                      <TableCell>£{record.national_insurance?.toLocaleString()}</TableCell>
                      <TableCell>£{record.pension_employee?.toLocaleString()}</TableCell>
                      <TableCell>£{record.total_deductions?.toLocaleString()}</TableCell>
                      <TableCell className="font-semibold text-green-600">£{record.net_pay?.toLocaleString()}</TableCell>
                      <TableCell>
                        <Badge className={
                          record.status === 'paid' ? 'bg-green-500' :
                          record.status === 'submitted_to_hmrc' ? 'bg-blue-500' :
                          'bg-yellow-500'
                        }>
                          {record.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {payrollRecords.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        No payroll records for this period. Run payroll to generate records.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* HMRC / RTI Tab */}
        <TabsContent value="hmrc" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  HMRC Real Time Information (RTI)
                </CardTitle>
                <CardDescription>Submit payroll data to HMRC</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Full Payment Submission (FPS)</h4>
                  <p className="text-sm text-blue-600 mb-3">
                    Submit employee payment details to HMRC on or before each payday.
                  </p>
                  {canSubmit && (
                    <Button onClick={handleSubmitFPS} className="bg-[#1e3a5f]">
                      <Send className="w-4 h-4 mr-2" />
                      Generate FPS for {selectedMonth}/{selectedYear}
                    </Button>
                  )}
                </div>
                
                <div className="bg-slate-50 border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Tax Year: 2024-25</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Personal Allowance: <span className="font-medium">£12,570</span></div>
                    <div>Basic Rate (20%): <span className="font-medium">up to £50,270</span></div>
                    <div>Higher Rate (40%): <span className="font-medium">£50,271 - £125,140</span></div>
                    <div>Additional Rate (45%): <span className="font-medium">over £125,140</span></div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  RTI Submission History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {rtiSubmissions.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No submissions yet</p>
                ) : (
                  <div className="space-y-2">
                    {rtiSubmissions.map((sub) => (
                      <div key={sub.id} className="flex items-center justify-between border-b pb-2">
                        <div>
                          <p className="font-medium">{sub.submission_type} - Period {sub.period}</p>
                          <p className="text-sm text-slate-500">{new Date(sub.submission_date).toLocaleString()}</p>
                        </div>
                        <Badge className={sub.status === 'pending' ? 'bg-yellow-500' : 'bg-green-500'}>
                          {sub.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Home Office Tab */}
        <TabsContent value="home-office" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-[#1e3a5f]">Home Office - Right to Work</h2>
            {canManage && (
              <Button onClick={() => setRtwDialogOpen(true)} className="bg-[#1e3a5f]">
                <Shield className="w-4 h-4 mr-2" />
                Record RTW Check
              </Button>
            )}
          </div>
          
          {/* Alerts */}
          {(rtwAlerts.expired?.length > 0 || rtwAlerts.expiring_30_days?.length > 0) && (
            <div className="grid md:grid-cols-2 gap-4">
              {rtwAlerts.expired?.length > 0 && (
                <Card className="border-red-300 bg-red-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-red-800 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Expired ({rtwAlerts.expired.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {rtwAlerts.expired.map((alert) => (
                      <div key={alert.id} className="border-b border-red-200 py-2 last:border-0">
                        <p className="font-medium">{alert.staff_name}</p>
                        <p className="text-sm text-red-600">Expired: {alert.expiry_date}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              
              {rtwAlerts.expiring_30_days?.length > 0 && (
                <Card className="border-amber-300 bg-amber-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-amber-800 flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Expiring in 30 Days ({rtwAlerts.expiring_30_days.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {rtwAlerts.expiring_30_days.map((alert) => (
                      <div key={alert.id} className="border-b border-amber-200 py-2 last:border-0">
                        <p className="font-medium">{alert.staff_name}</p>
                        <p className="text-sm text-amber-600">Expires: {alert.expiry_date}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          <Card>
            <CardHeader>
              <CardTitle>Visa Status Overview</CardTitle>
              <CardDescription>Right to Work status for all employees</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-100">
                    <TableHead>Employee</TableHead>
                    <TableHead>Visa Type</TableHead>
                    <TableHead>Last Check</TableHead>
                    <TableHead>Expiry Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payrollConfigs.map((config) => (
                    <TableRow key={config.staff_id}>
                      <TableCell className="font-medium">{config.staff_name}</TableCell>
                      <TableCell>
                        {VISA_TYPES.find(v => v.value === config.visa_type)?.label || config.visa_type || 'Not set'}
                      </TableCell>
                      <TableCell>{config.right_to_work_check_date || '-'}</TableCell>
                      <TableCell>
                        {config.visa_expiry || (config.visa_type === 'british_citizen' ? 'N/A' : '-')}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          config.visa_type === 'british_citizen' || config.visa_type === 'eu_settled' 
                            ? 'bg-green-500' 
                            : config.visa_expiry && new Date(config.visa_expiry) < new Date()
                              ? 'bg-red-500'
                              : 'bg-blue-500'
                        }>
                          {config.visa_type === 'british_citizen' || config.visa_type === 'eu_settled' 
                            ? 'Indefinite' 
                            : config.visa_expiry && new Date(config.visa_expiry) < new Date()
                              ? 'Expired'
                              : 'Valid'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Payroll Summary - {selectedMonth}/{selectedYear}</CardTitle>
              </CardHeader>
              <CardContent>
                {summary ? (
                  <div className="space-y-3">
                    <div className="flex justify-between border-b pb-2">
                      <span>Employees Paid</span>
                      <span className="font-semibold">{summary.employees_paid}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                      <span>Total Gross Pay</span>
                      <span className="font-semibold">£{summary.total_gross_pay?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                      <span>Total Income Tax</span>
                      <span className="font-semibold">£{summary.total_income_tax?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                      <span>Total NI (Employee)</span>
                      <span className="font-semibold">£{summary.total_ni_employee?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                      <span>Total NI (Employer)</span>
                      <span className="font-semibold">£{summary.total_ni_employer?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                      <span>Total Pension (Employee)</span>
                      <span className="font-semibold">£{summary.total_pension_employee?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                      <span>Total Pension (Employer)</span>
                      <span className="font-semibold">£{summary.total_pension_employer?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                      <span>Total Net Pay</span>
                      <span className="font-semibold text-green-600">£{summary.total_net_pay?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between pt-2">
                      <span className="font-semibold">Total Employer Cost</span>
                      <span className="font-bold text-[#1e3a5f]">£{summary.total_employer_cost?.toLocaleString()}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No data for this period</p>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Available Reports</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="w-4 h-4 mr-2" />
                  P60 End of Year Summary
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="w-4 h-4 mr-2" />
                  P45 Leaver Statement
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Download className="w-4 h-4 mr-2" />
                  Export Payslips (PDF)
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Download className="w-4 h-4 mr-2" />
                  Export to Accounting Software
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Employee Config Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Employee Payroll Configuration
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveConfig} className="space-y-6">
            {/* Employee Selection */}
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select value={configForm.staff_id} onValueChange={(v) => setConfigForm({...configForm, staff_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employee..." />
                </SelectTrigger>
                <SelectContent>
                  {staff.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Pay Details */}
            <div className="border rounded-lg p-4 space-y-4">
              <h4 className="font-semibold text-[#1e3a5f]">Pay Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Payment Type</Label>
                  <Select value={configForm.payment_type} onValueChange={(v) => setConfigForm({...configForm, payment_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="salary">Annual Salary</SelectItem>
                      <SelectItem value="hourly">Hourly Rate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Pay Frequency</Label>
                  <Select value={configForm.pay_frequency} onValueChange={(v) => setConfigForm({...configForm, pay_frequency: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="fortnightly">Fortnightly</SelectItem>
                      <SelectItem value="four_weekly">4-Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {configForm.payment_type === 'salary' ? (
                  <div className="space-y-2">
                    <Label>Annual Salary (£)</Label>
                    <Input 
                      type="number" 
                      value={configForm.annual_salary}
                      onChange={(e) => setConfigForm({...configForm, annual_salary: e.target.value})}
                      placeholder="e.g., 35000"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Hourly Rate (£)</Label>
                    <Input 
                      type="number" 
                      step="0.01"
                      value={configForm.hourly_rate}
                      onChange={(e) => setConfigForm({...configForm, hourly_rate: e.target.value})}
                      placeholder="e.g., 15.50"
                    />
                  </div>
                )}
              </div>
            </div>
            
            {/* HMRC Details */}
            <div className="border rounded-lg p-4 space-y-4">
              <h4 className="font-semibold text-[#1e3a5f]">HMRC Details</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Tax Code</Label>
                  <Select value={configForm.tax_code} onValueChange={(v) => setConfigForm({...configForm, tax_code: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TAX_CODES.map(code => (
                        <SelectItem key={code} value={code}>{code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>NI Category</Label>
                  <Select value={configForm.ni_category} onValueChange={(v) => setConfigForm({...configForm, ni_category: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {NI_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>NI Number</Label>
                  <Input 
                    value={configForm.ni_number}
                    onChange={(e) => setConfigForm({...configForm, ni_number: e.target.value.toUpperCase()})}
                    placeholder="AB123456C"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Student Loan</Label>
                  <Select value={configForm.student_loan_plan} onValueChange={(v) => setConfigForm({...configForm, student_loan_plan: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STUDENT_LOAN_PLANS.map(plan => (
                        <SelectItem key={plan.value} value={plan.value}>{plan.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Checkbox 
                    checked={configForm.postgrad_loan}
                    onCheckedChange={(v) => setConfigForm({...configForm, postgrad_loan: v})}
                  />
                  <Label>Postgraduate Loan</Label>
                </div>
              </div>
            </div>
            
            {/* Pension */}
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-[#1e3a5f]">Pension Auto-Enrollment</h4>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={configForm.pension_enrolled}
                    onCheckedChange={(v) => setConfigForm({...configForm, pension_enrolled: v})}
                  />
                  <Label>Enrolled</Label>
                </div>
              </div>
              {configForm.pension_enrolled && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Employee %</Label>
                    <Input 
                      type="number" 
                      step="0.1"
                      value={configForm.pension_employee_percent}
                      onChange={(e) => setConfigForm({...configForm, pension_employee_percent: parseFloat(e.target.value)})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Employer %</Label>
                    <Input 
                      type="number" 
                      step="0.1"
                      value={configForm.pension_employer_percent}
                      onChange={(e) => setConfigForm({...configForm, pension_employer_percent: parseFloat(e.target.value)})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Provider</Label>
                    <Input 
                      value={configForm.pension_provider}
                      onChange={(e) => setConfigForm({...configForm, pension_provider: e.target.value})}
                      placeholder="e.g., NEST, NOW Pensions"
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Home Office / Visa */}
            <div className="border rounded-lg p-4 space-y-4">
              <h4 className="font-semibold text-[#1e3a5f]">Home Office / Right to Work</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Visa Type</Label>
                  <Select value={configForm.visa_type} onValueChange={(v) => setConfigForm({...configForm, visa_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VISA_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {configForm.visa_type !== 'british_citizen' && (
                  <div className="space-y-2">
                    <Label>Visa Expiry Date</Label>
                    <Input 
                      type="date"
                      value={configForm.visa_expiry}
                      onChange={(e) => setConfigForm({...configForm, visa_expiry: e.target.value})}
                    />
                  </div>
                )}
              </div>
              {(configForm.visa_type === 'eu_settled' || configForm.visa_type === 'eu_pre_settled') && (
                <div className="space-y-2">
                  <Label>Share Code</Label>
                  <Input 
                    value={configForm.share_code}
                    onChange={(e) => setConfigForm({...configForm, share_code: e.target.value})}
                    placeholder="9 character share code"
                  />
                </div>
              )}
              {configForm.visa_type === 'skilled_worker' && (
                <div className="space-y-2">
                  <Label>CoS Number</Label>
                  <Input 
                    value={configForm.cos_number}
                    onChange={(e) => setConfigForm({...configForm, cos_number: e.target.value})}
                    placeholder="Certificate of Sponsorship number"
                  />
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1e3a5f]">Save Configuration</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Run Payroll Dialog */}
      <Dialog open={runPayrollDialogOpen} onOpenChange={setRunPayrollDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Run Payroll
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>This will calculate payroll for all configured employees for {selectedMonth}/{selectedYear}.</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <strong>Calculations include:</strong>
              <ul className="list-disc ml-4 mt-1 text-blue-700">
                <li>Income Tax (PAYE)</li>
                <li>National Insurance (Employee & Employer)</li>
                <li>Pension contributions (Auto-enrollment)</li>
                <li>Student Loan deductions</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRunPayrollDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRunPayroll} className="bg-[#1e3a5f]">Run Payroll</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RTW Check Dialog */}
      <Dialog open={rtwDialogOpen} onOpenChange={setRtwDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Record Right to Work Check
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordRTW} className="space-y-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select value={rtwForm.staff_id} onValueChange={(v) => setRtwForm({...rtwForm, staff_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employee..." />
                </SelectTrigger>
                <SelectContent>
                  {staff.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Visa Type *</Label>
                <Select value={rtwForm.visa_type} onValueChange={(v) => setRtwForm({...rtwForm, visa_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VISA_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Check Date *</Label>
                <Input 
                  type="date"
                  value={rtwForm.check_date}
                  onChange={(e) => setRtwForm({...rtwForm, check_date: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Document Type *</Label>
              <Input 
                value={rtwForm.document_type}
                onChange={(e) => setRtwForm({...rtwForm, document_type: e.target.value})}
                placeholder="e.g., Passport, BRP, Share Code"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Document Reference</Label>
                <Input 
                  value={rtwForm.document_reference}
                  onChange={(e) => setRtwForm({...rtwForm, document_reference: e.target.value})}
                  placeholder="Document number"
                />
              </div>
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input 
                  type="date"
                  value={rtwForm.expiry_date}
                  onChange={(e) => setRtwForm({...rtwForm, expiry_date: e.target.value})}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRtwDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1e3a5f]">Record Check</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
